<template>
  <div class="home-page" id="home-content"
       :class="{ 'skipping' : isSkipping, 'qr-active' : qrActive, 'loaded': hasLoaded, 'leaving': isLeaving }"
       @touchmove.prevent>

    <preloader v-if="$store.state.preloaderOn"/>

    <div class="bg" :style="gradBG"></div>
    <div class="home-inner-container">
      <canvas id="animatedBkg" ref="canvasBkg" :width="fullWidth" :height="fullHeight"></canvas>
      <noise class="fixed"/>

      <div class="homeWrap d-flex flex-column justify-content-center align-items-center" :class="{ ready: !qrActive }">

        <div class="qrWrap d-flex flex-column justify-content-center align-items-center">

          <div ref="noQR" class="noQR d-flex flex-column align-items-center justify-content-center"
               :class="{ ready: !qrActive }">
            <adobe-logo ref="logo" :class="{ 'logoLoaded': logoLoaded, 'finishedAnimation': finishedAnimation }"/>

            <div ref="nmd" class="mobile-decade-container">
              <next-mobile-decade :fill="'white'" class="nmd outro-item"/>
            </div>

            <transition name="fade" mode="out-in">
              <p ref="copy" class="fix-height" :key="isSkipping" v-html="isSkipping ? text2 : text1"></p>
            </transition>

            <a v-if="showViewOnMobile" ref="viewOnMobileBtn" class="view-mobile-btn" @click="viewOnMobileClick">
              <div class="cta-link" :class="{ ready: finishedAnimation }">
                <div class="inner-cover">
                  <span class="cta-text">Explore Now</span>
                  <span class="cta-text">Explore Now</span>
                </div>
              </div>
            </a>

            <div v-else ref="dragger" class="dragger" :class="{ isSkipping: isSkipping }">
              <a class="slide" href="#" @click.prevent="leavePage()">
                <transition name="fade" mode="out-in">
                  <span class="report-link" :key="isSkipping">
                    {{ isSkipping || !demoCapable ? 'View Report' : 'Personalize My Report' }}
                  </span>
                </transition>
              </a>
            </div>

            <a ref="skipPersonalize" v-show="demoCapable && isMobile" class="skip-link" href="#" @click.prevent="skip">
              <transition name="slide" mode="out-in">
                <span :key="isSkipping">{{ isSkipping ? 'Wait, I want to personalize my report.' : 'or Skip report personalization'}}</span>
              </transition>
            </a>
          </div>

          <div ref="qrContainer" class="qrContainer d-flex flex-column align-items-center justify-content-center"
               :class="{ ready: qrActive }">
            <div class="adobe-logo-container">
              <adobe-logo class="logoLoaded finishedAnimation"/>
            </div>
            <qr-content/>
          </div>

        </div>

        <div ref="viewOnMobileTxt" class="view-mobile-text" v-if="showViewOnMobile && !isIE">
          <p>
            You really should view this on mobile, but if you’re dead set against the best experience, check out the
            <router-link :to="'/report/0'">
              <span>desktop version</span>
            </router-link>
            .
            And if you really geek out about the numbers, take a look at the <a
            :href="pdfUrl"
            target="_blank">full survey results PDF</a>.
          </p>
        </div>

      </div>

    </div>

    <landscape-message ref="lsm" :override="tabletOverride"/>

  </div>
</template>

<script>
  import StateMixin from '@/components/report/mixins/StateMixin'
  import LandscapeMessage from '@/components/LandscapeMessage'
  import Preloader from '@/components/Preloader'
  import QrContent from '@/components/QrContent'
  import Noise from '@/components/demos/Noise'
  import NextMobileDecade from '@/components/svg/NextMobileDecade'
  import AdobeLogo from '@/components/svg/AdobeLogo'
  import Stats from '@/lib/CustomStats'
  import { randInt } from '@/lib/funcs'
  import { Bus, colors, pdfUrl } from '@/lib/consts'
  import { TweenMax, TimelineMax, Power2 } from 'TweenMax'

  export default {
    name: 'Home',
    mixins: [ StateMixin ],
    components: {
      LandscapeMessage,
      Noise,
      AdobeLogo,
      NextMobileDecade,
      QrContent,
      Preloader
    },
    data () {
      return {
        rafID: 0,
        stats: new Stats(),
        fpsCheck: true,
        fpsCount: 0,
        hasLoaded: false,
        logoLoaded: false,
        finishedAnimation: false,
        isSkipping: false,
        fullWidth: 0,
        fullHeight: 0,
        wWidth: 0,
        wHeight: 0,
        ctx: null,
        cGrad: null,
        circleRadius: 150,
        shapes: [],
        shapesIn: false,
        qrActive: false,
        preload: false,
        isLeaving: false,
        isPortrait: true,
        tweenTargets: [],
        pdfUrl: pdfUrl
      }
    },
    computed: {
      fps () {
        return this.stats.fps.toFixed( 2 )
      },
      scaleFactor () {
        if ( 'devicePixelRatio' in window ) {
          if ( window.devicePixelRatio > 1 ) {
            return window.devicePixelRatio
          }
        }
        return 1
      },
      cWidth () {
        return this.fullWidth * this.scaleFactor
      },
      cHeight () {
        return this.fullHeight * this.scaleFactor
      },
      showViewOnMobile () {
        return !this.isMobile
      },
      text1 () {
        let txt = '<span>Ten years ago, the world changed. Now, 500 mobile leaders tell Adobe how they’re preparing for the next ten.</span>'
        if ( this.demoCapable && this.isMobile ) txt += '<br><span>First, we\'ll use mobile technology to personalize your report.</span>'
        return txt
      },
      text2 () {
        return '<span>Since you skipped report personalization, it looks like you may be in a hurry so your report shows just the highlights.</span>' +
          '<br><span>Find the full report in the menu.</span>'
      },
      tabletOverride () {
        return this.formFactor === 'Tablet'
      },
      gradBG () {
        let bg = `background: ${this.colors[ 0 ]};`
        bg += `background: -moz-linear-gradient(-45deg, ${this.colors[ 0 ]} 0%, ${this.colors[ 1 ]} 115%);`
        bg += `background: -webkit-linear-gradient(-45deg, ${this.colors[ 0 ]} 0%,${this.colors[ 1 ]} 115%);`
        bg += `background: linear-gradient(135deg, ${this.colors[ 0 ]} 0%, ${this.colors[ 1 ]} 115%);`
        return bg
      }
    },
    watch: {
      fps () {
        this.fpsCount++
        if ( this.fpsCount >= 8 ) {
          this.fpsCheck = false
          if ( this.fps <= 20 ) {
            this.$store.dispatch( 'setDemoCapable', false )
          }
        }
      },
      formFactor () {
        this.onResize()
      }
    },
    methods: {
      initShapes () {
        this.shapes = []
        let rad1 = this.isMobile ? randInt( this.fullWidth / this.scaleFactor * 0.2, this.fullWidth / this.scaleFactor * 0.4 ) * this.scaleFactor : 210
        let rad2 = this.isMobile ? randInt( this.fullWidth / this.scaleFactor * 0.2, this.fullWidth / this.scaleFactor * 0.4 ) * this.scaleFactor : 150

        if ( this.formFactor === 'Tablet' && !this.isPortrait ) {
          rad1 = rad1 / 2
          rad2 = rad2 / 2
        }

        let xy = this.isMobile ? 0 : 100

        this.shapes.push( {
          type: 'circle',
          x: xy,
          y: xy,
          bgX1: 0,
          bgY1: 0,
          bgX2: rad1,
          bgY2: rad1,
          radius: rad1,
          wobbleSpeed: 600,
          opacity: 0,
          opacitySpeed: 0.01
        } )

        this.shapes.push( {
          type: 'circle',
          x: xy,
          y: this.fullHeight,
          bgX1: 0,
          bgY1: this.fullHeight,
          bgX2: rad2,
          bgY2: this.fullHeight - rad2,
          radius: rad2,
          wobbleSpeed: 500,
          opacity: 0,
          opacitySpeed: 0.005
        } )

        this.shapes.push( {
          type: 'rect',
          x: this.fullWidth,
          y: this.fullHeight,
          wx: this.isMobile && this.formFactor !== 'Tablet' ? 120 : 220,
          wy: this.isMobile && this.formFactor !== 'Tablet' ? 60 : 120,
          h: this.isMobile && this.formFactor !== 'Tablet' ? 60 : 120,
          angle: 0.5,
          wobbleSpeed: 900,
          opacity: 0,
          opacitySpeed: 0.003
        } )
      },
      routeAway () {
        this.isSkipping || !this.demoCapable ? this.$router.push( { path: '/report' } ) : this.$router.push( { path: '/demos' } )
      },
      leavePage () {
        this.isLeaving = true

        TweenMax.to( this.$el, 1, { opacity: 0, ease: Power2.easeInOut } )
        TweenMax.to( this.$refs.canvasBkg, 1, { scale: 1.5, ease: Power2.easeInOut, onComplete: this.routeAway } )
        this.tweenTargets.push( this.$refs.canvasBkg )
      },
      drawShapes () {
        let wobble
        this.ctx.clearRect( 0, 0, this.fullWidth, this.fullHeight )

        if ( this.isPortrait || this.formFactor === 'Tablet' ) {
          this.shapes.forEach( ( shape, index ) => {
            if ( shape.type === 'circle' ) {
              wobble = Math.sin( Date.now() / shape.wobbleSpeed ) * this.fullHeight * 0.0002

              shape.y = shape.y / 2 + wobble + shape.y / 2

              let bg = this.ctx.createLinearGradient( shape.bgX1, shape.bgY1, shape.bgX2, shape.bgY2 )
              bg.addColorStop( 0, this.colors[ 0 ] )
              bg.addColorStop( 1, this.colors[ 1 ] )

              this.ctx.beginPath()
              this.ctx.arc( shape.x, shape.y, shape.radius, 0, 2 * Math.PI )
              this.ctx.fillStyle = bg
              this.ctx.fill()
            } else {
              wobble = Math.sin( Date.now() / shape.wobbleSpeed ) * this.fullHeight * 0.0002

              shape.y = shape.y / 2 + wobble + shape.y / 2

              let bgSide = this.ctx.createLinearGradient( shape.x - shape.wx, shape.y - shape.wy * 2, shape.x, shape.y )
              bgSide.addColorStop( 0, this.colors[ 1 ] )
              bgSide.addColorStop( 1, this.colors[ 0 ] )

              let bgTop = this.ctx.createLinearGradient( shape.x - shape.wx, shape.y - shape.wy - shape.h / 2, shape.x * 1.25, shape.y - shape.h / 2 )
              bgTop.addColorStop( 0, this.colors[ 1 ] )
              bgTop.addColorStop( 1, this.colors[ 0 ] )

              // TOP
              this.ctx.beginPath()
              this.ctx.moveTo( shape.x, shape.y - shape.h )
              this.ctx.lineTo( shape.x - shape.wx, shape.y - shape.h - shape.wx * shape.angle )
              this.ctx.lineTo( shape.x - shape.wx + shape.wy, shape.y - shape.h - (shape.wx * 0.5 + shape.wy * shape.angle) )
              this.ctx.lineTo( shape.x + shape.wy, shape.y - shape.h - shape.wy * shape.angle )
              this.ctx.closePath()
              this.ctx.fillStyle = bgTop
              this.ctx.fill()

              // SIDE
              this.ctx.beginPath()
              this.ctx.moveTo( shape.x, shape.y )
              this.ctx.lineTo( shape.x - shape.wx, shape.y - shape.wx * shape.angle )
              this.ctx.lineTo( shape.x - shape.wx, shape.y - shape.h - shape.wx * shape.angle )
              this.ctx.lineTo( shape.x, shape.y - shape.h )
              this.ctx.lineTo( shape.x, shape.y )
              this.ctx.closePath()
              this.ctx.fillStyle = bgSide
              this.ctx.fill()
            }
          } )
        }

        this.rafID = requestAnimationFrame( () => {
          if ( this.fpsCheck ) this.stats.begin()
          this.drawShapes()
          if ( this.fpsCheck ) this.stats.end()
        } )
      },
      skip () {
        this.isSkipping ? this.isSkipping = false : this.isSkipping = true
      },
      onOrientationChange ( isPortrait ) {
        this.ctx.clearRect( 0, 0, this.cWidth, this.cHeight )
        this.isPortrait = isPortrait
        TweenMax.delayedCall( 0.25, this.onResize ) // derpy iOS
      },
      onResize () {
        this.fullWidth = window.innerWidth
        this.fullHeight = window.innerHeight
        this.$nextTick( () => {
          if ( this.$refs.canvasBkg ) {
            this.$refs.canvasBkg.width = this.fullWidth
            this.$refs.canvasBkg.height = this.fullHeight
          }
          this.initShapes()
        } )
      },
      viewOnMobileClick () {
        TweenMax.set( this.$refs.noQR, { transformOrigin: '50% 50%' } )
        TweenMax.to( this.$refs.noQR, 0.75, {
          opacity: 0,
          scale: 0.66,
          ease: Power2.easeIn
        } )
        TweenMax.delayedCall( 0.6, this.showQRCode )
      },
      showQRCode () {
        TweenMax.set( this.$refs.qrContainer, { opacity: 0, scale: 2, transformOrigin: '50% 50%' } )
        this.$nextTick( () => {
          this.qrActive = true
          TweenMax.to( this.$refs.qrContainer, 0.5, {
            opacity: 1,
            scale: 1,
            ease: Power2.easeOut
          } )
        } )
      },
      animateIn () {
        this.$nextTick( () => {
          let targetsMobile = [
            this.$refs.nmd,
            this.$refs.copy,
            this.$refs.dragger,
            this.$refs.skipPersonalize
          ]

          let targetsDesktop = [
            this.$refs.nmd,
            this.$refs.copy,
            this.$refs.viewOnMobileBtn,
            this.$refs.viewOnMobileTxt
          ]

          let targets = this.isMobile ? targetsMobile : targetsDesktop

          if ( this.$refs.canvasBkg ) {
            let tl = new TimelineMax( {
              delay: 0.75,
              onComplete: () => {
                this.finishedAnimation = true
              }
            } )

            targets.forEach( ( target, idx ) => {
              let opts = {
                delay: idx * 0.25,
                y: 0,
                opacity: 1,
                scale: 1,
                rotationX: 0,
                rotationY: 0,
                ease: Power2.easeInOut,
                clearProps: 'opacity'
              }

              if ( (this.isMobile && target === this.$refs.dragger) || (!this.isMobile && target === this.$refs.viewOnMobileBtn) ) {
                delete opts.clearProps
              }

              tl.to( target, 1.5, opts, 0 )
            } )

            TweenMax.to( this.$refs.canvasBkg, 3, { opacity: 0.6, ease: Power2.easeIn } )

            this.tweenTargets.push( tl )
          }
        } )
      },
      pickRandomColor () {
        // Pick random, except for the red one which clashes with the Adobe logo
        let c = randInt( 0, colors.length )
        if ( c === 3 ) {
          return this.pickRandomColor()
        }
        return c
      }
    },
    mounted () {
      this.$store.dispatch( 'setPreloader', true )
      this.$store.dispatch( 'setHomeViewed' )
      window.addEventListener( 'resize', this.onResize )

      // Double check the background doesn't clash with Adobe logo
      if ( this.colorIdx === 3 ) {
        let cIdx = this.pickRandomColor()
        this.$store.dispatch( 'setColors', cIdx )
      }

      Bus.$on( 'preloadFinished', () => {
        setTimeout( () => document.body.classList.add( 'show-gnav' ), 250 )
        this.logoLoaded = true
        this.preload = true
        this.animateIn()
      } )

      this.ctx = this.$refs.canvasBkg.getContext( '2d' )
      this.onResize()

      this.$nextTick( () => {
        let targets = [
          this.$refs.nmd,
          this.$refs.copy,
          this.$refs.viewOnMobileTxt,
          this.$refs.skipPersonalize
        ]

        TweenMax.set( targets, {
          y: 50,
          opacity: 0,
          rotationX: 20,
          rotationY: 50
        } )

        this.$refs.lsm.$on( 'orientationChange', this.onOrientationChange )
        this.onOrientationChange( this.isPortrait )
        this.drawShapes()
      } )
    },
    beforeDestroy () {
      TweenMax.killTweensOf( this.tweenTargets )
      cancelAnimationFrame( this.rafID )

      this.isLeaving = true
    }
  }
</script>

<style lang="scss">
  @import "~@/assets/scss/global/the_goods";
  @import "~@/assets/scss/global/classes/transitions";

  $slideHeight : 48px;
  $slideRadius : $slideHeight / 2;
  $slideWidth : auto;
  $slideBorderWidth : 1px;
  $skipTextHeight : 140px;

  .home-page {
    position  : fixed;
    top       : 0;
    left      : 0;
    width     : 100%;
    height    : 100%;
    overflow  : hidden;
    opacity   : 1;

    .bg {
      position  : absolute;
      top       : 0;
      left      : 0;
      width     : 100%;
      height    : 100%;
    }

    .home-inner-container {
      position : relative;
      top      : $gnavHeightMobile;
      height   : calc(100% - #{$gnavHeightMobile});

      @include screen-md-lg-xl {
        top     : $gnavHeightTablet;
        height  : calc(100% - #{$gnavHeightTablet});
      }
    }

    #animatedBkg {
      position         : fixed;
      left             : 0;
      top              : $gnavHeightMobile;
      width            : 100%;
      height           : calc(100% - #{$gnavHeightMobile});
      z-index          : 0;
      overflow         : hidden;
      transform        : scale(1);
      transform-origin : center;
      opacity          : 0;

      @include screen-md-lg-xl {
        top     : $gnavHeightTablet;
        height  : calc(100% - #{$gnavHeightTablet});
      }
    }

    .adobe-logo {
      width         : 100px;
      height        : 100px;
      transform     : scale(1);
      transition    : opacity .3s, transform .5s;
      margin-bottom : 20px;

      @media screen and (device-width : 320px) {
        width         : 75px;
        height        : 75px;
        margin-bottom : 10px;
      }

      @media screen and (device-width : 375px) {
        width         : 85px;
        height        : 85px;
        margin-bottom : 10px;
      }
    }

    .mobile-decade-container {

      .nmd {
        width     : 65vw;
        transform : translateZ(10px);

        @media screen and (device-width : 320px) {
          width : 50vw;
        }

        @media screen and (device-width : 360px) {
          width : 60vw;
        }

        @media screen and (device-width : 375px) {
          width : 60vw;
        }

        @include screen-md-lg-xl {
          max-height : 140px;
        }
      }
    }

    .homeWrap {
      width  : 100%;
      height : 100%;

      a {
        color       : white;
        font-size   : 14px;
        font-family : $adobe-clean;
        transition  : opacity 300ms ease-in-out;

        &:hover {
          opacity : 0.6;
        }

        &.skip-link {
          span {
            display         : inline-block;
            text-decoration : underline;
          }
          &:hover {
            opacity : 0.6 !important;
          }
        }
      }
    }

    .qrWrap {
      position  : relative;
      width     : 100%;
      flex-grow : 1;
    }

    .noQR,
    .qrContainer {
      position : absolute;
      top      : 0;
      left     : 0;
      width    : 100%;
      height   : 100%;
    }

    .noQR {
      pointer-events : none;

      &.ready {
        pointer-events : auto;
      }
    }

    .qrContainer {
      opacity        : 0;
      pointer-events : none;
      z-index        : 2;

      .adobe-logo-container {
        width  : 100px;
        height : 100px;
      }

      &.ready {
        pointer-events : auto;
      }
    }

    .fix-height {
      height          : $skipTextHeight;
      display         : flex;
      justify-content : center;
      align-items     : center;
      text-align      : center;
      flex-direction  : column;

      @media screen and (device-width: 320px) {
        height      : 120px;
        line-height : 1.2rem;
      }

      span {
        color : #fff;
      }

      @include screen-md-lg-xl {
        max-width : 450px;

        span {
          font-size   : rem(18);
          line-height : rem(30);
        }
      }
    }

    p {
      font-family : $adobe-clean-serif;
      font-size   : 1rem;
      font-weight : 400;
      font-style  : italic;
      width       : 75%;
      line-height : 1.4em;
      margin      : 1rem 0;

      @media screen and (device-width: 320px) {
        width  : 80%;
        margin : 0.5rem 0;
      }
    }

    .dragger {
      width             : 70%;
      margin-bottom     : 20px;
      max-width         : 350px;
      transform-origin  : 50% 50%;
      opacity           : 0;
      z-index           : 1;
      transform         : translateZ(1px) scale(0);
      transition        : width 0.5s ease, max-width 0.5s ease;

      @media screen and (device-width : 320px) {
        margin-bottom : 10px;
      }

      @media screen and (device-width : 375px) {
        margin-bottom : 10px;
      }

      &.isSkipping {
        width     : 50%;
        max-width : 250px;
      }

      .slide {
        height          : $slideHeight;
        width           : $slideWidth;
        border-radius   : $slideRadius;
        position        : relative;
        margin          : 0 20px;
        background      : $blue;
        display         : flex;
        justify-content : center;
        align-items     : center;

        span.report-link {
          width           : 100%;
          height          : 100%;
          position        : absolute;
          top             : 0px;
          left            : 0px;
          border-radius   : $slideRadius;
          display         : flex;
          justify-content : center;
          align-items     : center;
          text-decoration : none;
          font-size       : 1rem;
          pointer-events  : none;
          color           : #fff;
        }

        @media screen and (device-width: 375px) {
          height        : 40px;
          border-radius : 20px;

          span.report-link {
            font-size : 0.95rem;
          }
        }

        @media screen and (device-width: 320px) {
          height        : 32px;
          border-radius : 16px;
          margin        : 0;

          span.report-link {
            font-size : 0.95rem;
          }
        }
      }
    }

    &.qr-active {
      .fix-height,
      .view-mobile-btn,
      .mobile-decade-container {
        pointer-events : none;
      }
    }
  }

  .fps {
    position : fixed;
    top      : 10px;
    left     : 20px;
    color    : $white;
  }

  .view-mobile-btn {
    width            : auto;
    margin           : 0 auto;
    transform-origin : 50% 50%;
    opacity          : 0;
    z-index          : 1;
    transform        : translateZ(1px) scale(0);
  }

  .cta-link {
    display          : flex;
    align-items      : center;
    position         : relative;
    height           : $slideHeight;
    border-radius    : $slideRadius;
    background       : $blue;
    background-color : #3383e7;
    padding          : 0 60px;
    line-height      : 1.5;
    text-decoration  : none;
    text-align       : center;
    cursor           : pointer;
    will-change      : transform;

    .inner-cover {
      overflow : hidden;
      position : relative;
    }

    .cta-text {
      color     : #fff;
      font-size : 0.8125rem;
      display   : block;

      &:last-child {
        position : absolute;
        left     : 0;
        width    : 100%;
      }
    }

    &.ready {
      transition : all 0.5s cubic-bezier(0.645, 0.000, 0.355, 1.000);

      &:hover {
        text-decoration  : none;
        background-color : #2569bf;
        transform        : scale(1.08);

        .cta-text {
          -webkit-transform : translateY(-100%);
          transform         : translateY(-100%);
          font-size         : 0.8125rem;
          will-change       : transform;
        }
      }

      .cta-text {
        transition : transform 0.5s cubic-bezier(0.645, 0.000, 0.355, 1.000);
      }
    }
  }

  .view-mobile-text {
    margin-bottom : 30px;
    padding       : 0 $content-pad-x;

    p {
      font-size   : rem(14);
      line-height : rem(28);
      font-family : $adobe-clean;
      width       : 100%;
      max-width   : 610px;
      margin      : 0 auto;
      font-style  : normal;
      color       : white;

      a {
        text-decoration : underline;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Home.vue